import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import Avatar from './Avatar';
import { CloseIcon } from './icons';
import { formatListTime } from '../lib/datetime';

/**
 * Stories i helskärm, som går vidare av sig själva.
 *
 * Bilder visas en bestämd stund; filmer får spela klart, eftersom en
 * avklippt film är värre än en som dröjer. Ett tryck till höger hoppar
 * fram, till vänster tillbaka.
 */
const IMAGE_DURATION_MS = 5000;

export default function StoryViewer({ group, onClose, onSeen }) {
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const timer = useRef(null);
  const started = useRef(Date.now());

  const story = group.stories[index];

  const next = useCallback(() => {
    setIndex(current => {
      if (current + 1 < group.stories.length) return current + 1;
      onClose();
      return current;
    });
  }, [group.stories.length, onClose]);

  const previous = () => setIndex(current => Math.max(0, current - 1));

  // Markera som sedd sa fort den visas.
  useEffect(() => {
    if (!story) return;
    api.post(`/stories/${story.id}/seen`).catch(() => {});
    if (onSeen) onSeen(story.id);
  }, [story, onSeen]);

  // Bilder gar vidare pa tid; filmer nar de spelat klart.
  useEffect(() => {
    clearInterval(timer.current);
    setProgress(0);
    if (!story || story.media_type === 'video') return undefined;

    started.current = Date.now();
    timer.current = setInterval(() => {
      const share = (Date.now() - started.current) / IMAGE_DURATION_MS;
      if (share >= 1) next();
      else setProgress(share * 100);
    }, 50);

    return () => clearInterval(timer.current);
  }, [story, next]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') next();
      if (event.key === 'ArrowLeft') previous();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, onClose]);

  if (!story) return null;

  return (
    <div className="story-view">
      <div className="story-view__bars">
        {group.stories.map((item, i) => (
          <span key={item.id} className="story-view__bar">
            <span
              style={{ width: i < index ? '100%' : i === index ? `${progress}%` : '0%' }}
            />
          </span>
        ))}
      </div>

      <header className="story-view__top">
        <Avatar src={group.picture} name={group.username} size="sm" />
        <span className="story-view__who">
          <strong>{group.username}</strong>
          <small>{formatListTime(story.created_at)}</small>
        </span>
        <button className="story-view__close" onClick={onClose} aria-label="Stäng">
          <CloseIcon size={20} />
        </button>
      </header>

      <div className="story-view__media">
        {story.media_type === 'video' ? (
          <video src={story.media_path} autoPlay playsInline controls={false} onEnded={next} />
        ) : (
          <img src={story.media_path} alt="" />
        )}
      </div>

      {/* Osynliga ytor for att bläddra, som i en telefon. */}
      <button className="story-view__zone story-view__zone--prev" onClick={previous} aria-label="Föregående" />
      <button className="story-view__zone story-view__zone--next" onClick={next} aria-label="Nästa" />
    </div>
  );
}
